/**
 * Overview Diagram Hover Content Functionality
 * Handles touch, click, and keyboard navigation for hover content
 */

(function () {
	'use strict';

	/**
	 * Adjust popup position to ensure it stays within viewport
	 *
	 * @param {HTMLElement} popup - The popup element to adjust
	 * @param {HTMLElement} trigger - The trigger element that opens the popup
	 */
	function adjustPopupPosition(popup, trigger) {
		if (!popup || !trigger) return;

		// Ensure popup is visible before measuring
		const computedStyle = window.getComputedStyle(popup);
		if (
			computedStyle.display === 'none' ||
			computedStyle.visibility === 'hidden'
		) {
			return;
		}

		// Get viewport dimensions
		const viewportWidth =
			window.innerWidth || document.documentElement.clientWidth;
		const viewportHeight =
			window.innerHeight || document.documentElement.clientHeight;
		const padding = 10; // Minimum padding from viewport edges

		// Get fixed header height if it exists
		const header = document.querySelector(
			'header.is-fixed, .l-header.is-fixed, header[class*="is-fixed"]'
		);
		const headerHeight = header ? header.getBoundingClientRect().height : 0;
		const topOffset = padding + headerHeight; // Account for header + padding

		// Reset any previous inline style adjustments to get original position
		popup.style.left = '';
		popup.style.right = '';
		popup.style.top = '';
		popup.style.bottom = '';
		popup.style.marginBottom = '';
		popup.style.marginTop = '';
		popup.style.transform = '';

		// Force reflow to get accurate measurements after reset
		void popup.offsetHeight;

		// Get bounding boxes
		const popupRect = popup.getBoundingClientRect();

		// Calculate current position relative to viewport
		const popupLeft = popupRect.left;
		const popupRight = popupRect.right;
		const popupWidth = popupRect.width;
		const popupHeight = popupRect.height;

		// Check if popup has valid dimensions
		if (popupWidth === 0 || popupHeight === 0) {
			return;
		}

		// Get bounding box for trigger (after early return checks)
		const triggerRect = trigger.getBoundingClientRect();

		// Get the parent element (positioned ancestor) to calculate relative positioning
		const parentElement = popup.offsetParent;
		const parentRect = parentElement
			? parentElement.getBoundingClientRect()
			: { left: 0, top: 0 };

		// Check horizontal overflow and adjust
		if (popupRight > viewportWidth - padding) {
			// Popup goes off right edge
			// Check if we can flip to the left side of trigger
			if (triggerRect.left - popupWidth - padding >= padding) {
				// Enough space on the left - flip to left side
				popup.style.left = 'auto';
				popup.style.right = 'calc(100% + ' + padding + 'px)';
			} else {
				// Not enough space to flip, adjust to fit within viewport
				// Calculate desired left position in viewport coordinates
				const desiredLeft = viewportWidth - popupWidth - padding;
				// Convert to position relative to parent
				const newLeft = desiredLeft - parentRect.left;
				popup.style.left = `${newLeft}px`;
			}
		} else if (popupLeft < padding) {
			// Popup goes off left edge - position at padding from left edge
			const newLeft = padding - parentRect.left;
			popup.style.left = `${newLeft}px`;
		}

		// Force reflow after horizontal adjustment
		void popup.offsetHeight;

		// Recalculate after horizontal adjustment
		const updatedPopupRect = popup.getBoundingClientRect();
		const updatedPopupTop = updatedPopupRect.top;
		const updatedPopupBottom = updatedPopupRect.bottom;
		const updatedPopupHeight = updatedPopupRect.height;

		// Check vertical overflow and adjust
		const maxBottom = viewportHeight - padding;
		const minTop = topOffset; // Use topOffset which includes header height

		if (updatedPopupBottom > maxBottom) {
			// Popup goes off bottom edge
			// Try flipping above trigger first - but use TOP positioning, not bottom
			const spaceAbove = triggerRect.top - padding;
			if (spaceAbove >= updatedPopupHeight + padding) {
				// Calculate top position to place popup above trigger
				// We want popup bottom to be at trigger top minus padding
				const targetBottomInViewport = triggerRect.top - padding;
				const targetTopInViewport =
					targetBottomInViewport - updatedPopupHeight;

				// Convert to parent-relative coordinates
				const targetTopRelative = targetTopInViewport - parentRect.top;
				const minTopRelative = minTop - parentRect.top;

				// Use TOP positioning only (never use bottom)
				popup.style.bottom = '';
				popup.style.marginBottom = '';
				popup.style.top = `${Math.max(
					minTopRelative,
					targetTopRelative
				)}px`;

				void popup.offsetHeight;
				const flippedRect = popup.getBoundingClientRect();

				// Verify flip worked
				if (
					flippedRect.top >= minTop &&
					flippedRect.bottom <= maxBottom
				) {
					return; // Success - flip worked, using top positioning
				}

				// Flip failed - will continue to adjust below
				void popup.offsetHeight;
			}

			// Use TOP positioning only (clear bottom if it was set)
			popup.style.bottom = '';
			popup.style.marginBottom = '';

			// Calculate exact top position needed
			// We want: popup bottom = maxBottom
			// So: popup top = maxBottom - popup height
			const freshRect = popup.getBoundingClientRect();
			const targetTopInViewport = maxBottom - freshRect.height;

			// Convert from viewport coordinates to parent-relative coordinates
			const targetTopRelative = targetTopInViewport - parentRect.top;
			const minTopRelative = minTop - parentRect.top;

			// Apply the fix using TOP only
			popup.style.top = `${Math.max(
				minTopRelative,
				targetTopRelative
			)}px`;

			// Verify the fix worked
			void popup.offsetHeight;
			const verifyRect = popup.getBoundingClientRect();

			// If still overflowing, use more aggressive calculation
			if (verifyRect.bottom > maxBottom) {
				const actualHeight = verifyRect.height;
				const correctedTop = maxBottom - actualHeight - parentRect.top;
				popup.style.top = `${Math.max(minTopRelative, correctedTop)}px`;
			}
		} else if (updatedPopupTop < minTop) {
			// Popup goes off top edge - use TOP positioning only
			popup.style.bottom = '';
			popup.style.marginBottom = '';
			popup.style.top = `${minTop - parentRect.top}px`;
		} else if (popup.style.top || popup.style.bottom) {
			// Popup is within bounds - clear any inline styles to use CSS defaults
			// Only clear if we previously set them
			popup.style.top = '';
			popup.style.bottom = '';
			popup.style.marginBottom = '';
		}

		// FINAL SAFETY CHECK - ensure we're only using top
		void popup.offsetHeight;
		const finalCheck = popup.getBoundingClientRect();
		if (finalCheck.bottom > viewportHeight - padding) {
			// Clear bottom if set, use top only
			popup.style.bottom = '';
			popup.style.marginBottom = '';
			const emergencyTop =
				viewportHeight - padding - finalCheck.height - parentRect.top;
			const emergencyMinTop = padding - parentRect.top;
			popup.style.top = `${Math.max(emergencyMinTop, emergencyTop)}px`;
		}
	}

	// Initialize when DOM is ready
	function initOverviewDiagram() {
		// Store all active elements for outside click handling
		const activeElements = [];

		// Single document click listener for closing all open content
		document.addEventListener('click', function (e) {
			activeElements.forEach((element) => {
				if (!element.container.contains(e.target)) {
					element.container.setAttribute('aria-expanded', 'false');
					element.content.classList.remove('is-active');
				}
			});
		});

		// Handle first hover title wrapper (info icon)
		const firstHoverWrappers = document.querySelectorAll(
			'.overview-diagram__first-hover-title-wrapper'
		);

		firstHoverWrappers.forEach((wrapper) => {
			const hoverContent = wrapper.querySelector(
				'.overview-diagram__first-hover-title'
			);
			if (!hoverContent) return;

			// Make it focusable for keyboard navigation
			wrapper.setAttribute('tabindex', '0');
			wrapper.setAttribute('role', 'button');
			wrapper.setAttribute('aria-expanded', 'false');
			wrapper.setAttribute('aria-label', 'Show more information');

			// Store for outside click handling
			activeElements.push({ container: wrapper, content: hoverContent });

			// Toggle function
			function toggleContent(e) {
				if (e) {
					e.preventDefault();
					e.stopPropagation();
				}

				const isExpanded =
					wrapper.getAttribute('aria-expanded') === 'true';
				wrapper.setAttribute('aria-expanded', !isExpanded);
				hoverContent.classList.toggle('is-active');

				// Adjust position if opening
				if (!isExpanded) {
					// Use requestAnimationFrame to ensure DOM has fully updated
					window.requestAnimationFrame(() => {
						window.requestAnimationFrame(() => {
							adjustPopupPosition(hoverContent, wrapper);
						});
					});
				}
			}

			// Click/touch handler
			wrapper.addEventListener('click', toggleContent);

			// Hover handler - works same as click
			wrapper.addEventListener('mouseenter', function () {
				const isExpanded =
					wrapper.getAttribute('aria-expanded') === 'true';
				if (!isExpanded) {
					wrapper.setAttribute('aria-expanded', 'true');
					hoverContent.classList.add('is-active');

					// Adjust position after adding class
					window.requestAnimationFrame(() => {
						window.requestAnimationFrame(() => {
							adjustPopupPosition(hoverContent, wrapper);
						});
					});
				}
			});

			wrapper.addEventListener('mouseleave', function () {
				wrapper.setAttribute('aria-expanded', 'false');
				hoverContent.classList.remove('is-active');
			});

			// Keyboard handler
			wrapper.addEventListener('keydown', function (e) {
				if (e.key === 'Enter' || e.key === ' ') {
					toggleContent(e);
				}
			});
		});

		// Handle first content items
		const firstContentItems = document.querySelectorAll(
			'.overview-diagram__first-content-item'
		);

		firstContentItems.forEach((item) => {
			const hoverContent = item.querySelector(
				'.overview-diagram__first-hover-content'
			);
			if (!hoverContent) return;

			// Make it focusable for keyboard navigation
			item.setAttribute('tabindex', '0');
			item.setAttribute('role', 'button');
			item.setAttribute('aria-expanded', 'false');

			// Store for outside click handling
			activeElements.push({ container: item, content: hoverContent });

			// Toggle function
			function toggleContent(e) {
				const clickedElement = e.target;
				const isAnchorClick =
					clickedElement.tagName === 'A' ||
					clickedElement.closest('a') !== null;
				const isHoverContentClick =
					clickedElement === hoverContent ||
					hoverContent.contains(clickedElement);

				if (isAnchorClick || isHoverContentClick) {
					return;
				}

				if (e) {
					e.preventDefault();
					e.stopPropagation();
				}

				const isExpanded =
					item.getAttribute('aria-expanded') === 'true';
				item.setAttribute('aria-expanded', !isExpanded);
				hoverContent.classList.toggle('is-active');

				// Adjust position if opening
				if (!isExpanded) {
					// Use requestAnimationFrame to ensure DOM has fully updated
					window.requestAnimationFrame(() => {
						window.requestAnimationFrame(() => {
							adjustPopupPosition(hoverContent, item);
						});
					});
				}
			}

			// Click/touch handler
			item.addEventListener('click', toggleContent);

			// Hover handler - works same as click
			item.addEventListener('mouseenter', function () {
				const isExpanded =
					item.getAttribute('aria-expanded') === 'true';
				if (!isExpanded) {
					item.setAttribute('aria-expanded', 'true');
					hoverContent.classList.add('is-active');

					// Adjust position after adding class
					window.requestAnimationFrame(() => {
						window.requestAnimationFrame(() => {
							adjustPopupPosition(hoverContent, item);
						});
					});
				}
			});

			item.addEventListener('mouseleave', function () {
				item.setAttribute('aria-expanded', 'false');
				hoverContent.classList.remove('is-active');
			});

			// Keyboard handler
			item.addEventListener('keydown', function (e) {
				if (e.key === 'Enter' || e.key === ' ') {
					toggleContent(e);
				}
			});
		});

		// Handle second content items (Venn diagram circles)
		const secondContentItems = document.querySelectorAll(
			'.overview-diagram__second-content-item'
		);

		secondContentItems.forEach((item) => {
			const hoverContent = item.querySelector(
				'.overview-diagram__second-hover-content'
			);
			if (!hoverContent) return;

			// Make it focusable for keyboard navigation
			item.setAttribute('tabindex', '0');
			item.setAttribute('role', 'button');
			item.setAttribute('aria-expanded', 'false');

			// Store for outside click handling
			activeElements.push({ container: item, content: hoverContent });

			// Toggle function
			function toggleContent(e) {
				if (e) {
					e.preventDefault();
					e.stopPropagation();
				}

				const isExpanded =
					item.getAttribute('aria-expanded') === 'true';
				item.setAttribute('aria-expanded', !isExpanded);
				hoverContent.classList.toggle('is-active');

				// Set or reset z-index based on state (for second content items)
				if (!isExpanded) {
					item.style.zIndex = '5';
				} else {
					item.style.zIndex = '';
				}

				// Adjust position if opening
				if (!isExpanded) {
					// Use requestAnimationFrame to ensure DOM has fully updated
					window.requestAnimationFrame(() => {
						window.requestAnimationFrame(() => {
							adjustPopupPosition(hoverContent, item);
						});
					});
				}
			}

			// Click/touch handler
			item.addEventListener('click', toggleContent);

			// Hover handler - works same as click
			item.addEventListener('mouseenter', function () {
				const isExpanded =
					item.getAttribute('aria-expanded') === 'true';
				if (!isExpanded) {
					item.setAttribute('aria-expanded', 'true');
					hoverContent.classList.add('is-active');
					// Set z-index when active (for second content items)
					item.style.zIndex = '5';

					// Adjust position after adding class
					window.requestAnimationFrame(() => {
						window.requestAnimationFrame(() => {
							adjustPopupPosition(hoverContent, item);
						});
					});
				}
			});

			item.addEventListener('mouseleave', function () {
				item.setAttribute('aria-expanded', 'false');
				hoverContent.classList.remove('is-active');
				// Reset z-index when inactive (for second content items)
				item.style.zIndex = '';
			});

			// Keyboard handler
			item.addEventListener('keydown', function (e) {
				if (e.key === 'Enter' || e.key === ' ') {
					toggleContent(e);
				}
			});
		});

		// Adjust popup positions on window resize - works for both click and hover
		let resizeTimeout;
		window.addEventListener('resize', function () {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(function () {
				activeElements.forEach((element) => {
					// Check if popup is visible (either via is-active class or CSS hover)
					const isActive =
						element.content.classList.contains('is-active');
					const isVisible =
						window.getComputedStyle(element.content).display !==
						'none';
					if (isActive || isVisible) {
						adjustPopupPosition(element.content, element.container);
					}
				});
			}, 100);
		});

		// Adjust popup positions on scroll - works for both click and hover
		let scrollTimeout;
		window.addEventListener(
			'scroll',
			function () {
				clearTimeout(scrollTimeout);
				scrollTimeout = setTimeout(function () {
					activeElements.forEach((element) => {
						// Check if popup is visible (either via is-active class or CSS hover)
						const isActive =
							element.content.classList.contains('is-active');
						const isVisible =
							window.getComputedStyle(element.content).display !==
							'none';
						if (isActive || isVisible) {
							adjustPopupPosition(
								element.content,
								element.container
							);
						}
					});
				}, 50);
			},
			true
		);
	}

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initOverviewDiagram);
	} else {
		initOverviewDiagram();
	}
})();
